import { canonicalAssetLanguage } from "./vod-title";
export const LOCALISED_TEXT_FIELDS = [
    { key: "title", label: "Title" },
    { key: "synopsisShort", label: "Short description" },
    { key: "synopsisMedium", label: "Medium description" },
    { key: "synopsisLong", label: "Long description" },
];
export const LOCALISED_TEXT_KEYS = LOCALISED_TEXT_FIELDS.map((f) => f.key);
export const LOCALISED_STORAGE_LIMITS = {
    title: 255,
    synopsisShort: 65535,
    synopsisMedium: 65535,
    synopsisLong: 65535,
};
export const LOCALISED_BYTE_MEASURED = [
    "synopsisShort",
    "synopsisMedium",
    "synopsisLong",
];
export const LOCALISED_ADVISORY_LENGTHS = {
    title: 200,
    synopsisShort: 250,
    synopsisMedium: 1000,
    synopsisLong: 5000,
};
export function utf8ByteLength(value) {
    return new TextEncoder().encode(value).length;
}
export function localisedFieldLength(field, value) {
    return LOCALISED_BYTE_MEASURED.includes(field)
        ? { unit: "bytes", length: utf8ByteLength(value) }
        : { unit: "characters", length: value.length };
}
export function advisoryOverruns(row) {
    if (!row)
        return [];
    const out = [];
    for (const key of LOCALISED_TEXT_KEYS) {
        const value = typeof row[key] === "string" ? row[key] : "";
        const advisory = LOCALISED_ADVISORY_LENGTHS[key];
        if (value.length > advisory) {
            out.push({ field: key, length: value.length, advisory });
        }
    }
    return out;
}
export function emptyLocalisedRow(language) {
    var _a;
    return {
        language: (_a = canonicalAssetLanguage(language)) !== null && _a !== void 0 ? _a : String(language),
        title: "",
        synopsisShort: "",
        synopsisMedium: "",
        synopsisLong: "",
    };
}
export function isBlankLocalisedRow(row) {
    if (!row)
        return true;
    return LOCALISED_TEXT_KEYS.every((key) => (typeof row[key] === "string" ? row[key] : "").trim() === "");
}
export function localisedStatus(row) {
    if (!row)
        return "missing";
    const values = LOCALISED_TEXT_KEYS.map((key) => (typeof row[key] === "string" ? row[key] : "").trim());
    if (values.every((v) => !v))
        return "missing";
    if (values.every((v) => v))
        return "done";
    return "partial";
}
export function localisedSummary(row) {
    const status = localisedStatus(row);
    if (status === "missing")
        return "Not configured";
    if (status === "done")
        return "Title and descriptions complete.";
    const missing = LOCALISED_TEXT_FIELDS.find((f) => (typeof row[f.key] === "string" ? row[f.key] : "").trim() === "");
    return missing ? `${missing.label} missing` : "Incomplete";
}
export function localisedRows(asset) {
    return Array.isArray(asset.localizedInfo) ? asset.localizedInfo : [];
}
export function localisedLanguages(asset) {
    const seen = new Set();
    for (const row of localisedRows(asset)) {
        const code = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language);
        if (code)
            seen.add(code);
    }
    return [...seen].sort();
}
export function localisedByLanguage(asset) {
    const out = new Map();
    for (const row of localisedRows(asset)) {
        const code = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language);
        if (!code)
            continue;
        out.set(code, row);
    }
    return out;
}
export function localisedFor(asset, language) {
    const code = canonicalAssetLanguage(language);
    if (!code)
        return undefined;
    return localisedByLanguage(asset).get(code);
}
export function countLocalised(asset, languages) {
    const byLanguage = localisedByLanguage(asset);
    const codes = languages
        ? languages.map((l) => { var _a; return (_a = canonicalAssetLanguage(l)) !== null && _a !== void 0 ? _a : l; })
        : [...byLanguage.keys()];
    let filled = 0;
    for (const code of codes) {
        if (localisedStatus(byLanguage.get(code)) === "done")
            filled += 1;
    }
    return { filled, total: codes.length };
}
export function orphanedLanguages(asset, keeping) {
    const kept = new Set(keeping
        .map((l) => canonicalAssetLanguage(l))
        .filter((l) => !!l));
    const out = [];
    for (const [code, row] of localisedByLanguage(asset)) {
        if (kept.has(code))
            continue;
        if (localisedStatus(row) === "missing")
            continue;
        out.push(code);
    }
    return out.sort();
}
export function localisedIssues(asset) {
    var _a;
    const rows = localisedRows(asset);
    if (rows.length === 0)
        return [{ kind: "no_localised_text" }];
    const out = [];
    for (const row of rows) {
        const code = canonicalAssetLanguage(row === null || row === void 0 ? void 0 : row.language);
        if (!code) {
            out.push({
                kind: "unstorable_language",
                language: String((_a = row === null || row === void 0 ? void 0 : row.language) !== null && _a !== void 0 ? _a : ""),
            });
            continue;
        }
        const status = localisedStatus(row);
        if (status === "missing") {
            out.push({ kind: "blank_language", language: code });
            continue;
        }
        if (status === "partial") {
            out.push({
                kind: "incomplete_language",
                language: code,
                missing: LOCALISED_TEXT_FIELDS.filter((f) => (typeof row[f.key] === "string" ? row[f.key] : "").trim() === "").map((f) => f.key),
            });
        }
    }
    return out;
}
export function readLocalizedInfo(raw) {
    if (!Array.isArray(raw)) {
        if (raw && typeof raw === "object") {
            return {
                refusal: {
                    error: "collection_must_be_array",
                    field: "localizedInfo",
                    hint: "`localizedInfo` is an array of rows upstream, each carrying its own `language`, not an object keyed by language. Send [{language, title, synopsisShort, synopsisMedium, synopsisLong}].",
                },
            };
        }
        return {
            refusal: { error: "collection_must_be_array", field: "localizedInfo" },
        };
    }
    const out = [];
    const seen = new Map();
    for (const [index, entry] of raw.entries()) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
            return {
                refusal: { error: "localized_info_row_must_be_object", index },
            };
        }
        const row = entry;
        const language = canonicalAssetLanguage(row.language);
        if (!language) {
            return {
                refusal: {
                    error: "invalid_localised_language",
                    index,
                    value: row.language,
                    hint: "`LocalizedInfo.language` is VarChar(3): a lowercase 2-3 letter code, no region suffix. `pt-BR` cannot be stored, and it is the primary key beside assetId, so a wrong one is a row nothing can address.",
                },
            };
        }
        const first = seen.get(language);
        if (first !== undefined) {
            return {
                refusal: {
                    error: "duplicate_localised_language",
                    language,
                    index,
                    firstIndex: first,
                    hint: "`LocalizedInfo` is PRIMARY KEY (assetId, language) under a case-insensitive collation, so one language is one row - `sv` and `SV` are the same row. Upstream a duplicate is a bare 500 on create and a silent last-wins on update. State the language once.",
                },
            };
        }
        seen.set(language, index);
        const values = {};
        for (const key of LOCALISED_TEXT_KEYS) {
            const value = row[key];
            if (value !== undefined && value !== null && typeof value !== "string") {
                return {
                    refusal: {
                        error: "localised_field_must_be_text",
                        field: key,
                        index,
                        language,
                        value,
                    },
                };
            }
            const text = typeof value === "string" ? value : "";
            const limit = LOCALISED_STORAGE_LIMITS[key];
            const measured = localisedFieldLength(key, text);
            if (measured.length > limit) {
                return {
                    refusal: {
                        error: "field_too_long",
                        field: key,
                        index,
                        language,
                        max: limit,
                        unit: measured.unit,
                        actual: measured.length,
                        hint: measured.unit === "bytes"
                            ? "MySQL TEXT holds 65 535 BYTES and the column is utf8mb4, so accented characters cost two and emoji four. Strict mode makes an over-length value an error, not a truncation - and it surfaces as a 500 that names nothing. Nothing here trims it for you."
                            : "`LocalizedInfo.title` is VarChar(255). Nothing here trims it for you - a truncated title is somebody's copy lost between the form and the catalogue.",
                    },
                };
            }
            values[key] = text;
        }
        out.push({ language, ...values });
    }
    return { value: out };
}
